import { sendSearchPageVideoClick } from '@/analytics/ga';
import { SendVideoClickParams } from '@/analytics/types';
import { getCountry, getSexualOrientation } from '@/utils/general';
import { BaseVideoClickTracker } from '@/analytics/base_video_click_tracker';
import { RecommendationStore } from '@/stores/recommendation/recommendation';
import { RecommendationSource } from '@/stores/recommendation/types';

export const MAX_ITEM_POSITION = 100;
export const MAX_PAGE_NUMBER = 4;

export class TrackVideoClick extends BaseVideoClickTracker {
    private isLoggedIn: boolean;
    private searchKeyword: string;

    constructor(
        isLoggedIn: boolean,
        searchKeyword: string,
        recommendationStore: RecommendationStore,
        source: RecommendationSource
    ) {
        super(recommendationStore, source);
        this.isLoggedIn = isLoggedIn;
        this.searchKeyword = searchKeyword;
    }

    getTrackingData(videoItem: HTMLElement): SendVideoClickParams {
        const itemPosition = this._getVideoItemPosition(videoItem);
        const videoId = this._getVideoItemId(videoItem);
        const pageNumber = this._getCurrentPageNumber(this.$root);

        const countryCode = getCountry();
        const orientation = getSexualOrientation();

        return {
            videoId,
            pageNumber,
            itemPosition,
            countryCode: countryCode,
            orientation: orientation,
            keyword: this.searchKeyword,
            loggedIn: this.isLoggedIn,
        };
    }

    isTrackingDataValid(trackingData: SendVideoClickParams): boolean {
        if (
            trackingData.videoId < 0 ||
            trackingData.pageNumber < 1 ||
            trackingData.pageNumber > MAX_PAGE_NUMBER ||
            trackingData.itemPosition < 1 ||
            trackingData.itemPosition > MAX_ITEM_POSITION
        ) {
            return false;
        }

        if (!trackingData.keyword || !trackingData.countryCode || !trackingData.orientation) {
            return false;
        }

        return true;
    }

    handleVideoItemClick(trackingData: SendVideoClickParams, recommendationId: string): void {
        this.sendGaEvent(trackingData);
        this.sendRecommendationClickEvent({
            video_id: trackingData.videoId,
            page_number: trackingData.pageNumber,
            keyword: trackingData.keyword,
            item_position: trackingData.itemPosition,
            recommendation_id: recommendationId,
        });
    }

    sendGaEvent(data: SendVideoClickParams) {
        sendSearchPageVideoClick(data);
    }

    /**
     * Returns the current page number from the root element's data attribute.
     *
     * @returns {number} - -1 if the page number is invalid.
     */
    private _getCurrentPageNumber(rootEl: HTMLElement): number {
        const page = rootEl.getAttribute('data-page');

        try {
            const pageNumber = parseInt(page, 10);

            if (pageNumber < 1) {
                return -1;
            }

            return pageNumber;
        } catch (error) {
            return -1;
        }
    }
}
